import React from 'react';
import { Box, Center, HStack, Skeleton, VStack } from 'native-base';
import { faBirthdayCake, faEnvelope, faHome, faPhone, faUserCircle } from '@fortawesome/free-solid-svg-icons';
import { Member, useAppSettings, useI18n } from '@springbig/shared';
import { SpIcon } from '~/shared/Icon';
import { Caption, H1 } from '~/shared/Typography';
import { useGeneralStyles, useProfileStyles, useReferStyles } from '~/shared/styles';
import { useAppNavigate } from '~/pages';
import Button from '~/shared/Button';
import dayjs from 'dayjs';
import QrCode from '~/shared/QrCode';

type ProfileHeaderProps = {
    member?: Member | null;
}

const ProfileHeader = ({ member }: ProfileHeaderProps) => {

    const { t } = useI18n();
    const { navigate, params } = useAppNavigate();
    const { profile } = useAppSettings();
    const {
        text,
        icon
    } = useProfileStyles();
    const gStyles = useGeneralStyles();
    const { qr_code_color, qr_code_background } = useReferStyles();

    const formatEmail = (inputEmail: string): string => {

        if (inputEmail.length >= 33) {
            return inputEmail.slice(0, 33) + "...";
        }

        return inputEmail;
    }

    return (
        <VStack
            px={'4'}
            w={'100%'}
            alignItems={'center'}
            space={4}
        >
            <VStack alignItems='center' space={4}>
                <H1
                    fontWeight={'normal'}
                    text={'profile.greetings'}
                    textValues={[`${member?.first_name || ''} ${member?.last_name || ''}`]}
                    color={text}
                />
                {
                    profile?.profileQRCode && (<Box p={3} bg={qr_code_background}
                        borderRadius={'md'}
                        w={'48'}
                        h={'48'}
                    >
                        {member && member.phone_number && <QrCode
                            color={qr_code_color}
                            size={170}
                            value={member && member.phone_number || ''}
                        />}
                    </Box>)
                }
                {
                    profile.profileIcon && (
                        <SpIcon
                            color={icon}
                            icon={faUserCircle}
                            size={120}
                        />
                    )
                }
                <HStack space={4}>
                    <Button
                        testID='profile-recent-history-button'
                        bg={gStyles.button_background}
                        flex={1}
                        px={4}
                        onPress={() => navigate(`w/${params.wallet_id}/profile/menu/history`)}
                    >
                        <Caption
                            color={gStyles.button_text}
                            text='profileMenu.recent_history'
                        />
                    </Button>
                    <Button
                        testID='profile-my-account-button'
                        bg={gStyles.button_background}
                        flex={1}
                        px={4}
                        onPress={() => navigate(`w/${params.wallet_id}/profile/menu`)}
                    >
                        <Caption
                            color={gStyles.button_text}
                            text='profile.my_account'
                        />
                    </Button>
                </HStack>
            </VStack>
            <VStack alignSelf={'flex-start'} space={3}>
                {member && member.phone_number && (
                    <HStack alignItems={'center'} space={2}>
                        <SpIcon
                            color={icon}
                            icon={faPhone}
                        />
                        <Caption
                            color={text}
                        ><Caption fontWeight={500}>{t('profile.phone')}</Caption> {member?.phone_number}</Caption>
                    </HStack>
                )}
                {member && member.email && (
                    <HStack alignItems={'center'} space={2}>
                        <SpIcon
                            color={icon}
                            icon={faEnvelope}
                        />
                        <Caption
                            color={text}
                            overflowX={'hidden'}
                        ><Caption fontWeight={500}>{t('profile.email')}</Caption> {formatEmail(member.email)}</Caption>
                    </HStack>
                )}
                {member && member.birthday && (
                    <HStack alignItems={'center'} space={2}>
                        <SpIcon
                            color={icon}
                            icon={faBirthdayCake}
                        />
                        <Caption
                            color={text}
                        ><Caption fontWeight={500}>{t('profile.birthday')}</Caption> {dayjs(member.birthday).format('MM/DD/YYYY')}</Caption>
                    </HStack>
                )}
                {member?.address1 && (
                    <HStack alignItems={'center'} space={2}>
                        <SpIcon
                            color={icon}
                            icon={faHome}
                        />
                        <Caption
                            color={text}
                        >{member.address1 || ''} {member.address2 || ''}</Caption>
                    </HStack>
                )}
            </VStack>
        </VStack>
    );
};


export const ProfileHeaderSkeleton = () => {
    return (
        <VStack space={6} alignItems="center" mt={8} w='100%'>
            <Skeleton
                w={'175px'}
                h={5}
                rounded={'md'}
            />
            <HStack space={4}>
                <Skeleton
                    h={10}
                    w={32}
                    rounded="md"
                />
                <Skeleton
                    h={10}
                    w={32}
                    rounded="md"
                />
            </HStack>
            <VStack space={2} alignItems="flex-start" w='100%'>
                <HStack space={2} alignItems="center">
                    <Center h={6} w={6}>
                        <Skeleton
                            h={4}
                            w={4}
                            rounded="full"
                        />
                    </Center>
                    <Skeleton.Text
                        lines={1}
                        width={80}
                    />
                </HStack>
                <HStack space={2} alignItems="center">
                    <Center h={6} w={6}>
                        <Skeleton
                            h={4}
                            w={4}
                            rounded="full"
                        />
                    </Center>
                    <Skeleton.Text
                        lines={1}
                        width={80}
                    />
                </HStack>
                <HStack space={2} alignItems="center">
                    <Center h={6} w={6}>
                        <Skeleton
                            h={4}
                            w={4}
                            rounded="full"
                        />
                    </Center>
                    <Skeleton.Text
                        lines={1}
                        width={40}
                    />
                </HStack>
            </VStack>
        </VStack>
    );
};

export default ProfileHeader;
